import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  UtensilsCrossed,
  Sparkles,
  ShoppingCart,
  Clock,
  MapPin,
  Phone,
  Hotel,
  Plus,
  Minus,
  Check,
  ListOrdered,
  CreditCard,
  Receipt,
  ChefHat,
  Loader2,
  AlertCircle,
  FileText,
  Wallet,
  IndianRupee,
  Shield,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from 'lucide-react';
import MizuDialer from '@/components/MizuDialer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate device fingerprint
const getDeviceFingerprint = () => {
  const stored = localStorage.getItem('guest_portal_device_fp');
  if (stored) return stored;
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  
  const fingerprint = btoa(
    navigator.userAgent + 
    navigator.language + 
    screen.width + 'x' + screen.height + 
    new Date().getTimezoneOffset() +
    canvas.toDataURL()
  ).slice(0, 32);
  
  localStorage.setItem('guest_portal_device_fp', fingerprint);
  return fingerprint;
};

export default function GuestPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalInfo, setPortalInfo] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [orders, setOrders] = useState({ food_orders: [], service_orders: [] });
  const [cart, setCart] = useState([]);
  const [serviceCart, setServiceCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false);
  const [activeTab, setActiveTab] = useState('menu');
  const [billData, setBillData] = useState(null);
  const [payingBill, setPayingBill] = useState(false);
  
  // Bill detail expansion states
  const [showFoodDetails, setShowFoodDetails] = useState(false);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  
  // SIP credentials for room's assigned extension
  const [sipCredentials, setSipCredentials] = useState(null);
  
  // OTP verification state
  const [needsOTP, setNeedsOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [demoOtp, setDemoOtp] = useState('');
  const [deviceFingerprint] = useState(getDeviceFingerprint());

  // Check device verification
  const checkDevice = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/guest-portal/${token}/check-device`, {
        device_fingerprint: deviceFingerprint
      });
      
      if (response.data.requires_otp) {
        setNeedsOTP(true);
        return false;
      }
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify device');
      return false;
    }
  }, [token, deviceFingerprint]);

  // Send OTP
  const sendOTP = async () => {
    setSendingOTP(true);
    try {
      const response = await axios.post(`${API}/guest-portal/${token}/send-device-otp`, {
        device_fingerprint: deviceFingerprint
      });
      
      setOtpSent(true);
      setOtpSentTo(response.data.sent_to);
      if (response.data.demo_otp) {
        setDemoOtp(response.data.demo_otp);
      }
      toast.success('OTP sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }
    
    setVerifyingOTP(true);
    try {
      await axios.post(`${API}/guest-portal/${token}/verify-device-otp`, {
        device_fingerprint: deviceFingerprint,
        otp: otp
      });
      
      setNeedsOTP(false);
      toast.success('Device verified successfully');
      loadPortalData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifyingOTP(false);
    }
  };

  // Fetch portal info
  const fetchPortalInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${token}`);
      setPortalInfo(response.data);
      setError(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired portal link');
      return false;
    }
  }, [token]);

  // Fetch menu
  const fetchMenu = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${token}/menu`);
      setMenuCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    }
  }, [token]);

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${token}/services`);
      setServiceGroups(response.data.service_groups || []);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  }, [token]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${token}/orders`);
      setOrders(response.data);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  }, [token]);

  // Fetch bill summary
  const fetchBill = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${token}/bill`);
      setBillData(response.data);
    } catch (err) {
      console.error('Failed to fetch bill:', err);
    }
  }, [token]);

  // Fetch SIP credentials for room's assigned extension
  const fetchSipCredentials = useCallback(async (hotelId, roomNumber) => {
    try {
      const response = await axios.get(`${API}/sip/room-credentials/${hotelId}/${roomNumber}`);
      if (response.data.configured) {
        setSipCredentials(response.data);
        console.log('SIP credentials loaded for room:', roomNumber);
      } else {
        console.log('SIP not configured for room:', response.data.message);
        setSipCredentials(null);
      }
    } catch (err) {
      console.error('Failed to fetch SIP credentials:', err);
      setSipCredentials(null);
    }
  }, []);

  // Load all portal data
  const loadPortalData = useCallback(async () => {
    setLoading(true);
    const infoLoaded = await fetchPortalInfo();
    if (infoLoaded) {
      await Promise.all([fetchMenu(), fetchServices(), fetchOrders(), fetchBill()]);
    }
    setLoading(false);
  }, [fetchPortalInfo, fetchMenu, fetchServices, fetchOrders, fetchBill]);

  // Fetch SIP credentials when portal info is loaded
  useEffect(() => {
    if (portalInfo?.hotel?.id && portalInfo?.booking?.room_number) {
      fetchSipCredentials(portalInfo.hotel.id, portalInfo.booking.room_number);
    }
  }, [portalInfo, fetchSipCredentials]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const deviceVerified = await checkDevice();
      if (deviceVerified) {
        await loadPortalData();
      } else {
        // Still fetch basic info to show hotel name in OTP screen
        await fetchPortalInfo();
      }
      setLoading(false);
    };
    init();
  }, [checkDevice, loadPortalData, fetchPortalInfo]);

  // Cart functions for food
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1, type: 'food' }];
    });
    toast.success(`Added ${item.name} to cart`);
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === itemId);
      if (existing && existing.quantity > 1) {
        return prev.map(i => i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i);
      }
      return prev.filter(i => i.id !== itemId);
    });
  };

  // Cart functions for services
  const addServiceToCart = (service) => {
    setServiceCart(prev => {
      const existing = prev.find(s => s.id === service.id);
      if (existing) {
        return prev.map(s => s.id === service.id ? { ...s, quantity: s.quantity + 1 } : s);
      }
      return [...prev, { ...service, quantity: 1, type: 'service' }];
    });
    toast.success(`Added ${service.name} to cart`);
  };

  const removeServiceFromCart = (serviceId) => {
    setServiceCart(prev => {
      const existing = prev.find(s => s.id === serviceId);
      if (existing && existing.quantity > 1) {
        return prev.map(s => s.id === serviceId ? { ...s, quantity: s.quantity - 1 } : s);
      }
      return prev.filter(s => s.id !== serviceId);
    });
  };

  const getCartTotal = () => {
    const foodTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const serviceTotal = serviceCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return foodTotal + serviceTotal;
  };

  const getCartItemCount = () => {
    const foodCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const serviceCount = serviceCart.reduce((sum, item) => sum + item.quantity, 0);
    return foodCount + serviceCount;
  };

  // Place combined order (food + services)
  const placeOrder = async (paymentMethod) => {
    if (cart.length === 0 && serviceCart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setPlacingOrder(true);
    try {
      // Place food orders if any
      if (cart.length > 0) {
        const foodOrderData = {
          items: cart.map(item => ({
            item_id: item.id,
            item_name: item.name,
            quantity: item.quantity,
            price: item.price,
            notes: null
          })),
          notes: orderNotes || null,
          payment_method: paymentMethod
        };
        await axios.post(`${API}/guest-portal/${token}/food-order`, foodOrderData);
      }

      // Place service orders if any
      for (const service of serviceCart) {
        for (let i = 0; i < service.quantity; i++) {
          await axios.post(`${API}/guest-portal/${token}/service-order`, {
            service_id: service.id,
            quantity: 1,
            payment_method: paymentMethod
          });
        }
      }
      
      toast.success('Order placed successfully!');
      setCart([]);
      setServiceCart([]);
      setOrderNotes('');
      setCartOpen(false);
      fetchOrders();
      fetchBill();
      setActiveTab('orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  // Pay bill via Razorpay
  const handlePayBill = async () => {
    if (!billData || billData.summary.balance_due <= 0) {
      toast.info('No balance due');
      return;
    }

    setPayingBill(true);
    try {
      const response = await axios.post(`${API}/guest-portal/${token}/pay-bill`, {
        amount: billData.summary.balance_due
      });

      if (response.data.razorpay_order_id) {
        const options = {
          key: response.data.razorpay_key_id,
          amount: response.data.amount * 100,
          currency: 'INR',
          name: portalInfo?.hotel?.name || 'Hotel',
          description: `Bill Payment - Room ${portalInfo?.booking?.room_number}`,
          order_id: response.data.razorpay_order_id,
          handler: async function (paymentResponse) {
            try {
              await axios.post(`${API}/guest-portal/${token}/verify-payment`, {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              });
              toast.success('Payment successful!');
              fetchBill();
            } catch (err) {
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            name: portalInfo?.guest?.name,
            email: portalInfo?.guest?.email,
            contact: portalInfo?.guest?.phone
          },
          theme: {
            color: '#7c3aed'
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.info(response.data.message || 'Payment gateway not configured');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPayingBill(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-violet-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !needsOTP) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Portal Unavailable</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact the front desk.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OTP Verification Screen
  if (needsOTP) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center">
              <Shield className="h-8 w-8 text-violet-600" />
            </div>
            <CardTitle className="text-xl">Verify Your Device</CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              {portalInfo?.hotel?.name || 'Hotel'} Guest Portal
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {!otpSent ? (
              <>
                <p className="text-center text-gray-600">
                  For your security, we need to verify this device. An OTP will be sent to your registered contact.
                </p>
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  onClick={sendOTP}
                  disabled={sendingOTP}
                  data-testid="send-otp-button"
                >
                  {sendingOTP ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4 mr-2" />
                      Send OTP
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <div className="text-center space-y-2">
                  <p className="text-gray-600">OTP sent to</p>
                  <p className="font-medium text-violet-700">{otpSentTo}</p>
                  {demoOtp && (
                    <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
                      Demo Mode - OTP: {demoOtp}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  onClick={verifyOTP}
                  disabled={verifyingOTP || otp.length !== 6}
                  data-testid="verify-otp-button"
                >
                  {verifyingOTP ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Verify OTP
                    </>
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={sendOTP}
                  disabled={sendingOTP}
                >
                  Resend OTP
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, guest, hotel } = portalInfo || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-violet-900 flex items-center gap-2">
                <Hotel className="h-5 w-5" />
                {hotel?.name || 'Hotel'}
              </h1>
              <p className="text-sm text-gray-500">Welcome, {guest?.name}</p>
            </div>
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="relative" data-testid="cart-button">
                  <ShoppingCart className="h-4 w-4" />
                  {getCartItemCount() > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-violet-600">
                      {getCartItemCount()}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Your Cart
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                  {cart.length === 0 && serviceCart.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Your cart is empty</p>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-3 pr-4">
                          {/* Food Items */}
                          {cart.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                <UtensilsCrossed className="h-3 w-3" /> Food Items
                              </p>
                              {cart.map(item => (
                                <div key={item.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg mb-2">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{item.name}</p>
                                    <p className="text-sm text-gray-500">₹{item.price}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => removeFromCart(item.id)}>
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                    <span className="w-6 text-center font-medium">{item.quantity}</span>
                                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => addToCart(item)}>
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Service Items */}
                          {serviceCart.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> Services
                              </p>
                              {serviceCart.map(service => (
                                <div key={service.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-2">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{service.name}</p>
                                    <p className="text-sm text-gray-500">₹{service.price}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => removeServiceFromCart(service.id)}>
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                    <span className="w-6 text-center font-medium">{service.quantity}</span>
                                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => addServiceToCart(service)}>
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                      
                      <Separator />
                      
                      <Textarea
                        placeholder="Special instructions (optional)"
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        className="h-20"
                        data-testid="order-notes"
                      />
                      
                      <div className="flex justify-between items-center text-lg font-semibold">
                        <span>Subtotal</span>
                        <span>₹{getCartTotal().toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-500">Taxes will be calculated at checkout</p>
                      
                      <div className="space-y-2 pt-2">
                        <Button
                          className="w-full bg-violet-600 hover:bg-violet-700"
                          onClick={() => placeOrder('room_bill')}
                          disabled={placingOrder}
                          data-testid="add-to-bill-button"
                        >
                          {placingOrder ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Receipt className="h-4 w-4 mr-2" />
                          )}
                          Add to Room Bill
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => placeOrder('online')}
                          disabled={placingOrder}
                          data-testid="pay-now-button"
                        >
                          {placingOrder ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CreditCard className="h-4 w-4 mr-2" />
                          )}
                          Pay Now
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Room Info Banner */}
      <div className="bg-violet-100 border-b border-violet-200">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="font-medium text-violet-900">
              Room {booking?.room_number || 'N/A'}
            </span>
            <span className="text-violet-700">{booking?.room_type}</span>
          </div>
          <Badge variant="outline" className="bg-white">
            <Clock className="h-3 w-3 mr-1" />
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="menu" className="flex items-center gap-1" data-testid="menu-tab">
              <UtensilsCrossed className="h-4 w-4" />
              Menu
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-1" data-testid="services-tab">
              <Sparkles className="h-4 w-4" />
              Services
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-1" data-testid="orders-tab">
              <ListOrdered className="h-4 w-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="bill" className="flex items-center gap-1" data-testid="bill-tab">
              <FileText className="h-4 w-4" />
              My Bill
            </TabsTrigger>
          </TabsList>

          {/* Menu Tab */}
          <TabsContent value="menu" className="space-y-6">
            {menuCategories.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <UtensilsCrossed className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No menu items available</p>
                </CardContent>
              </Card>
            ) : (
              menuCategories.map(category => (
                <div key={category.category_id}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ChefHat className="h-5 w-5 text-violet-600" />
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.items.map(item => (
                      <Card key={item.id} className="overflow-hidden" data-testid={`menu-item-${item.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{item.name}</h4>
                              {item.description && (
                                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                              )}
                              <p className="text-violet-600 font-semibold mt-2">₹{item.price}</p>
                            </div>
                            <Button
                              size="sm"
                              className="bg-violet-600 hover:bg-violet-700"
                              onClick={() => addToCart(item)}
                              data-testid={`add-to-cart-${item.id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Services Tab - Now with Add to Cart */}
          <TabsContent value="services" className="space-y-6">
            {serviceGroups.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No services available for ordering</p>
                </CardContent>
              </Card>
            ) : (
              serviceGroups.map(group => (
                <div key={group.name}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-violet-600" />
                    {group.name}
                  </h3>
                  <div className="space-y-3">
                    {group.services.map(service => (
                      <Card key={service.id} data-testid={`service-item-${service.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{service.name}</h4>
                              {service.description && (
                                <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                              )}
                              {service.price > 0 && (
                                <p className="text-violet-600 font-semibold mt-2">₹{service.price}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              className="bg-violet-600 hover:bg-violet-700"
                              onClick={() => addServiceToCart(service)}
                              data-testid={`add-service-${service.id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            {orders.food_orders.length === 0 && orders.service_orders.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <ListOrdered className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No orders yet</p>
                  <p className="text-sm text-gray-400 mt-1">Your orders will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {orders.food_orders.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <UtensilsCrossed className="h-5 w-5 text-violet-600" />
                      Food Orders
                    </h3>
                    <div className="space-y-3">
                      {orders.food_orders.map(order => (
                        <Card key={order.id} data-testid={`food-order-${order.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex gap-2 mb-2">
                                  <Badge className={
                                    order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    order.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }>
                                    {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                                  </Badge>
                                  <Badge variant="outline" className={
                                    order.payment_status === 'paid' ? 'border-green-500 text-green-700' : 'border-violet-500 text-violet-700'
                                  }>
                                    {order.payment_status === 'paid' ? 'Paid' : 'Room Bill'}
                                  </Badge>
                                </div>
                                <div className="space-y-1">
                                  {order.items?.map((item, idx) => (
                                    <p key={idx} className="text-sm text-gray-600">
                                      {item.quantity}x {item.item_name}
                                    </p>
                                  ))}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-violet-600">₹{order.total_amount?.toFixed(2)}</p>
                                <p className="text-xs text-gray-400">
                                  {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {orders.service_orders.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-violet-600" />
                      Service Requests
                    </h3>
                    <div className="space-y-3">
                      {orders.service_orders.map(order => (
                        <Card key={order.id} data-testid={`service-order-${order.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex gap-2 mb-2">
                                  <Badge className={
                                    order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }>
                                    {order.status?.charAt(0).toUpperCase() + order.status?.slice(1).replace('_', ' ')}
                                  </Badge>
                                  <Badge variant="outline" className={
                                    order.payment_status === 'paid' ? 'border-green-500 text-green-700' : 'border-violet-500 text-violet-700'
                                  }>
                                    {order.payment_status === 'paid' ? 'Paid' : 'Room Bill'}
                                  </Badge>
                                </div>
                                <p className="font-medium">{order.service_name}</p>
                              </div>
                              <div className="text-right">
                                {order.amount > 0 && (
                                  <p className="font-semibold text-violet-600">₹{order.amount?.toFixed(2)}</p>
                                )}
                                <p className="text-xs text-gray-400">
                                  {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Bill Tab - Updated with Paid/Room Bill status and Details */}
          <TabsContent value="bill" className="space-y-6">
            {!billData ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Loader2 className="h-12 w-12 mx-auto text-violet-300 animate-spin mb-2" />
                  <p className="text-gray-500">Loading bill...</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="border-violet-200">
                  <CardHeader className="pb-3 bg-violet-50">
                    <CardTitle className="flex items-center gap-2 text-violet-900">
                      <FileText className="h-5 w-5" />
                      Current Bill Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-4">
                    {/* Room Charges */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Hotel className="h-4 w-4 text-violet-600" />
                          Room Charges
                        </span>
                        <Badge variant="outline" className="border-violet-500 text-violet-700">Room Bill</Badge>
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Room Rate</span>
                          <span>₹{billData.room_charges.room_rate?.toFixed(2)} × {billData.room_charges.total_nights} nights</span>
                        </div>
                        {billData.room_charges.discount > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount</span>
                            <span>-₹{billData.room_charges.discount?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax</span>
                          <span>₹{billData.room_charges.tax_amount?.toFixed(2)}</span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-medium">
                          <span>Room Total</span>
                          <span>₹{billData.summary.room_total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Food Orders - With Expandable Details */}
                    {billData.summary.food_total > 0 && (
                      <Collapsible open={showFoodDetails} onOpenChange={setShowFoodDetails}>
                        <div className="space-y-2">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                              <h4 className="font-medium text-gray-700 flex items-center justify-between w-full">
                                <span className="flex items-center gap-2">
                                  <UtensilsCrossed className="h-4 w-4 text-orange-600" />
                                  Food & Beverages
                                  <span className="text-orange-600 font-semibold">₹{billData.summary.food_total?.toFixed(2)}</span>
                                </span>
                                {showFoodDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </h4>
                            </Button>
                          </CollapsibleTrigger>
                          
                          {/* Summary View */}
                          <div className="space-y-2">
                            {billData.food_orders.paid_total > 0 && (
                              <div className="bg-green-50 rounded-lg p-3 text-sm">
                                <div className="flex justify-between font-medium text-green-700">
                                  <span className="flex items-center gap-1">
                                    <Check className="h-4 w-4" /> Paid
                                  </span>
                                  <span>₹{billData.food_orders.paid_total?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                            {billData.food_orders.room_bill_total > 0 && (
                              <div className="bg-orange-50 rounded-lg p-3 text-sm">
                                <div className="flex justify-between font-medium text-orange-700">
                                  <span className="flex items-center gap-1">
                                    <Receipt className="h-4 w-4" /> Room Bill
                                  </span>
                                  <span>₹{billData.food_orders.room_bill_total?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Detailed View */}
                          <CollapsibleContent>
                            <div className="mt-3 space-y-2 border-t pt-3">
                              <p className="text-xs font-medium text-gray-500">Order Details:</p>
                              {billData.food_orders.items?.map((order, idx) => (
                                <div key={order.id || idx} className="bg-white border rounded-lg p-3 text-sm">
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                      <Badge className={order.display_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                                        {order.display_status}
                                      </Badge>
                                      <span className="text-xs text-gray-400">
                                        {order.created_at ? new Date(order.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                                      </span>
                                    </div>
                                    <span className="font-semibold">₹{order.total_amount?.toFixed(2)}</span>
                                  </div>
                                  <div className="space-y-1 text-gray-600">
                                    {order.items?.map((item, itemIdx) => (
                                      <div key={itemIdx} className="flex justify-between">
                                        <span>{item.quantity}x {item.item_name}</span>
                                        <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    )}

                    {/* Service Orders - With Expandable Details */}
                    {billData.summary.service_total > 0 && (
                      <Collapsible open={showServiceDetails} onOpenChange={setShowServiceDetails}>
                        <div className="space-y-2">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                              <h4 className="font-medium text-gray-700 flex items-center justify-between w-full">
                                <span className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4 text-blue-600" />
                                  Services
                                  <span className="text-blue-600 font-semibold">₹{billData.summary.service_total?.toFixed(2)}</span>
                                </span>
                                {showServiceDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </h4>
                            </Button>
                          </CollapsibleTrigger>
                          
                          {/* Summary View */}
                          <div className="space-y-2">
                            {billData.service_orders.paid_total > 0 && (
                              <div className="bg-green-50 rounded-lg p-3 text-sm">
                                <div className="flex justify-between font-medium text-green-700">
                                  <span className="flex items-center gap-1">
                                    <Check className="h-4 w-4" /> Paid
                                  </span>
                                  <span>₹{billData.service_orders.paid_total?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                            {billData.service_orders.room_bill_total > 0 && (
                              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                                <div className="flex justify-between font-medium text-blue-700">
                                  <span className="flex items-center gap-1">
                                    <Receipt className="h-4 w-4" /> Room Bill
                                  </span>
                                  <span>₹{billData.service_orders.room_bill_total?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Detailed View */}
                          <CollapsibleContent>
                            <div className="mt-3 space-y-2 border-t pt-3">
                              <p className="text-xs font-medium text-gray-500">Service Details:</p>
                              {billData.service_orders.items?.map((order, idx) => (
                                <div key={order.id || idx} className="bg-white border rounded-lg p-3 text-sm">
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                      <Badge className={order.display_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                                        {order.display_status}
                                      </Badge>
                                      <span className="font-medium">{order.service_name}</span>
                                    </div>
                                    <span className="font-semibold">₹{order.amount?.toFixed(2)}</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {order.created_at ? new Date(order.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    )}

                    <Separator />

                    {/* Grand Total */}
                    <div className="bg-gray-100 rounded-lg p-4">
                      <div className="flex justify-between items-center text-lg font-semibold text-gray-800">
                        <span>Grand Total</span>
                        <span>₹{billData.summary.grand_total?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Summary with Paid vs Balance */}
                    <div className="space-y-3">
                      {/* Total Paid - Always show if any payment made */}
                      <div className={`rounded-lg p-4 ${billData.summary.total_paid > 0 ? 'bg-green-100' : 'bg-gray-50'}`}>
                        <div className={`flex justify-between items-center ${billData.summary.total_paid > 0 ? 'text-green-800' : 'text-gray-500'}`}>
                          <span className="font-medium flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5" />
                            Total Paid
                          </span>
                          <span className="text-lg font-semibold">₹{billData.summary.total_paid?.toFixed(2)}</span>
                        </div>
                        {billData.summary.total_paid > 0 && (
                          <div className="text-xs text-green-600 mt-2 space-y-1">
                            {billData.summary.advance_paid > 0 && (
                              <p>• Advance: ₹{billData.summary.advance_paid?.toFixed(2)}</p>
                            )}
                            {billData.summary.food_paid > 0 && (
                              <p>• Food (Paid Online): ₹{billData.summary.food_paid?.toFixed(2)}</p>
                            )}
                            {billData.summary.service_paid > 0 && (
                              <p>• Services (Paid Online): ₹{billData.summary.service_paid?.toFixed(2)}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Balance Due */}
                      <div className="bg-violet-100 rounded-lg p-4">
                        <div className="flex justify-between items-center text-xl font-bold text-violet-900">
                          <span className="flex items-center gap-2">
                            <IndianRupee className="h-5 w-5" />
                            Balance Due
                          </span>
                          <span>₹{billData.summary.balance_due?.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-violet-600 mt-1">To be settled at checkout</p>
                      </div>
                    </div>

                    {/* Pay Now Button */}
                    {billData.summary.balance_due > 0 && (
                      <Button
                        className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base"
                        onClick={handlePayBill}
                        disabled={payingBill}
                        data-testid="pay-bill-button"
                      >
                        {payingBill ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-5 w-5 mr-2" />
                            Pay ₹{billData.summary.balance_due?.toFixed(2)} Now
                          </>
                        )}
                      </Button>
                    )}

                    {billData.summary.balance_due <= 0 && (
                      <div className="text-center py-4 text-green-600 font-medium flex items-center justify-center gap-2">
                        <Check className="h-5 w-5" />
                        Bill Fully Paid
                      </div>
                    )}
                  </CardContent>
                </Card>

                <p className="text-xs text-center text-gray-400">
                  Bill is updated in real-time. For any discrepancies, please contact the front desk.
                </p>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Hotel Info Footer */}
      <footer className="bg-gray-50 border-t mt-8">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="text-center space-y-2">
            <h4 className="font-semibold text-gray-900">{hotel?.name}</h4>
            {hotel?.address && (
              <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
                <MapPin className="h-4 w-4" />
                {hotel.address}
              </p>
            )}
            {hotel?.phone && (
              <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
                <Phone className="h-4 w-4" />
                {hotel.phone}
              </p>
            )}
            <p className="text-xs text-gray-400 pt-4">
              Powered by Bizionix HMS
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Cart Button (Mobile) */}
      {getCartItemCount() > 0 && !cartOpen && (
        <div className="fixed bottom-4 left-4 right-4 max-w-lg mx-auto md:hidden">
          <Button
            className="w-full bg-violet-600 hover:bg-violet-700 h-14 text-base shadow-lg"
            onClick={() => setCartOpen(true)}
            data-testid="floating-cart-button"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            View Cart ({getCartItemCount()} items) - ₹{getCartTotal().toFixed(2)}
          </Button>
        </div>
      )}
      
      {/* Mizu SIP Dialer for guest calls - Only show if SIP is configured for the room */}
      {hotel?.id && sipCredentials?.configured && (
        <MizuDialer 
          hotelId={hotel.id} 
          isPortal={true}
          sipConfig={sipCredentials.server}
          guestCredentials={{
            username: sipCredentials.credentials.username,
            password: sipCredentials.credentials.password,
            display_name: sipCredentials.credentials.display_name || guest?.name || 'Guest',
            extension: sipCredentials.credentials.extension
          }}
        />
      )}
    </div>
  );
}
